// @flow

import { getPolopolyImageElement, getImageElement } from '../utils/imageUtils'
import throttle from 'lodash/throttle'

import { background, uncroppedImageContainer } from '../stylesheets/background.css'
import { alignCenter, alignMiddle } from '../stylesheets/alignment/index.css'

let eventListener = null

type onScrollHandlerTypeProps = {
  polopolyId: ?string,
  backgroundImage?: string,
  backgroundColor: ?string,
  alignment?: Array<string>,
  uncropped?: boolean
}

export const triggerOnScrollComponent = {
  type: 'trigger-on-scroll',
  render(id: string, props: onScrollHandlerTypeProps, bgInnerHTML: ?string = ''): string {
    const {
      backgroundImage,
      polopolyId,
      alignment,
      uncropped,
      backgroundColor = '#333'
    } = props
    const style = `background-color: ${backgroundColor || ''};`
    // const style = `background-color: ${backgroundColor};`

    const alignmentClasses = alignment ?
      alignment.join(' ') :
      [alignMiddle, alignCenter].join(' ')

    const imageStyle = uncropped ?
      'position: relative' :
      'min-width: 100%; min-height: 100%; position: relative;'

    let imageElement = ''
    if (polopolyId) {
      imageElement = getPolopolyImageElement(polopolyId, alignmentClasses, imageStyle)
    } else if (backgroundImage) {
      imageElement = getImageElement(backgroundImage, alignmentClasses, imageStyle)
    }

    return `
      <div id="${id}" class="${background}" style="${style}">
        ${uncropped ? `<div class="${uncroppedImageContainer}">` : ''}
          ${imageElement}
          ${uncropped ? `</div>` : ''}

          ${bgInnerHTML}
      </div>
    `
  },
  onCurrentChanged(currentSection) {
    if (eventListener) {
      document.removeEventListener('scroll', eventListener)
      eventListener = null
    }

    const { type } = currentSection
    if (type === 'trigger-on-scroll') {
      eventListener = throttle(() => handleTriggerOnScroll(currentSection), 30, {
        passive: true
      })
      document.addEventListener('scroll', eventListener)
    }
  }
}

const handleTriggerOnScroll = section => {
  const { y, top, height } = section.foregroundElement.getBoundingClientRect()
  const { innerHeight, innerWidth } = window
  const { actions, lastParsedHeight, lastScroll } = section.props.onScroll

  let actionsToDo = actions
  const direction = lastScroll > (y || top) ? 'forwards' : 'backwards'

  const shouldReparseWhen =
    !lastParsedHeight ||
    lastParsedHeight > innerWidth + 50 ||
    lastParsedHeight < innerWidth - 50

  if (shouldReparseWhen) {
    section.props.onScroll.lastParsedHeight = innerWidth

    actionsToDo = actions.map(action => {
      return {
        ...action,
        parsedWhen: parseWhen(action.when, height),
        parsedBreakpoints: innerWidth
      }
    })
  }

  if (direction !== section.props.onScroll.direction) actionsToDo.reverse()

  section.props.onScroll.lastScroll = y || top
  section.props.onScroll.direction = direction

  let pxInView = innerHeight - (y || top)

  if (pxInView >= height) {
    pxInView = height
  } else if (pxInView <= 0) {
    pxInView = 0
  }

  section.props.onScroll.actions = actionsToDo.map((action, i) => {
    let { breakpoint, parsedWhen, isAnimated, classes, style } = action

    const isBetweenBreakpoints =
      innerWidth > breakpoint.from && innerWidth < breakpoint.to

    let shouldAnimateIn = parsedWhen.from < pxInView && parsedWhen.to > pxInView
    if (direction === 'backwards') {
      shouldAnimateIn = parsedWhen.to > pxInView && parsedWhen.from < pxInView
    }

    if (shouldAnimateIn && !isAnimated && isBetweenBreakpoints) {
      // DO ANIMATION

      const targetElement = getTarget(action, section)

      doAnimation(targetElement, classes, style)
      isAnimated = true
    } else if (!shouldAnimateIn && isAnimated) {
      // UNDO ANIMATION
      if (!action.once) {
        const targetElement = getTarget(action, section)

        isAnimated = false
        undoAnimation(targetElement, classes, style)
      }
    }
    return {
      ...action,
      isAnimated
    }
  })
}

const parseWhen = (bp, height = 1) => {
  const parseFunction = val => {
    if (typeof val !== 'number') {
      if (val.indexOf('%') > -1) {
        return height * (parseFloat(val) / 100)
      }
      return parseInt(val)
    }
  }

  return {
    to: parseFunction(bp.to),
    from: parseFunction(bp.from)
  }
}

const getTarget = (action, section) => {
  const { target } = action

  if (!target) return section.backgroundElement
  if (Array.isArray(target)) {
    if (target.length > 1) {
      const foregroundOrBackground =
        target[1] === 'foreground' ? 'foregroundElement' : 'backgroundElement'

      return [...section[foregroundOrBackground].querySelectorAll(target[0])]
    }
    return [...section.foregroundElement.querySelectorAll(target[0])]
  }
  if (typeof target === 'string') {
    if (target === 'foreground') return section.foregroundElement
    if (target === 'background') return section.backgroundElement
    return document.querySelector(target)
  }
}

const undoAnimation = (targetElement, classes, style) => {
  const isTargetArray = Array.isArray(targetElement)

  if (targetElement && classes) {
    if (isTargetArray) {
      targetElement.map(tE => tE.classList.remove(classes))
    } else {
      targetElement.classList.remove(classes)
    }
  }
  if (targetElement && style) {
    if (isTargetArray) {
      targetElement.map(tE => {
        Object.keys(style).forEach(s => {
          tE.style[s] = null
        })
      })
    } else {
      Object.keys(style).forEach(s => {
        targetElement.style[s] = null
      })
    }
  }
}

const doAnimation = (targetElement, classes, style) => {
  const isTargetArray = Array.isArray(targetElement)
  if (targetElement && classes) {
    if (isTargetArray) {
      targetElement.map(tE => tE.classList.add(classes))
    } else {
      targetElement.classList.add(classes)
    }
  }
  if (targetElement && style) {
    if (isTargetArray) {
      targetElement.map(tE => {
        Object.keys(style).forEach(s => {
          tE.style[s] = style[s]
        })
      })
    } else {
      Object.keys(style).forEach(s => {
        targetElement.style[s] = style[s]
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/components/triggerOnScroll.js